import React, { FunctionComponent } from 'react';

import { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { goBack, CallHistoryMethodAction, push } from 'connected-react-router';

import AppState from '@webapp/store/state';

import moment from 'moment';

import { Translate } from 'react-localize-redux';

import styled, { css } from 'styled-components';

import { getWidth } from '@components/styles';

import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';
import Grid from '@components/basics/grid';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import { ButtonLoadingCtrl } from '@webapp/controllers/button-loading';

interface UnsubscribePageProps {
  className?: string;
  isMobile: boolean;
  nextBillingDate: string;
  goBack?: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => {
  const artistSubscriptions = state.subscriptionManager.summary?.artists.find(
    (a) => a.id === state.artist.active?.id
  );
  const tracPro = artistSubscriptions?.subscriptions.find(
    (s) => s.type === 'trac-pro'
  );

  let nextDate = tracPro?.nextBilling || '';

  if (
    tracPro?.subscription.trial_ends &&
    moment(tracPro.subscription.trial_ends).isAfter(moment())
  ) {
    nextDate = tracPro.subscription.trial_ends;
  }

  return {
    isMobile: state.ui.mobile,
    nextBillingDate: nextDate,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goBack: () => dispatch(goBack()),
});

const UnsubscribePage: FunctionComponent<UnsubscribePageProps> = ({
  className,
  isMobile,
  nextBillingDate,
  goBack,
}) => {
  const dispatch = useDispatch();

  const handleDowngrade = () => dispatch(push(`/unsubscribe/poll`));

  return (
    <MotionOpacity className={className || ''}>
      <Container>
        <TitleWrapper>
          <Typography type="subheader">
            <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.TITLE" />
          </Typography>
          <Typography type="title" weight="400" opacity="0.5">
            <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.TEXT" />
          </Typography>
        </TitleWrapper>
        <Card active={true}>
          <div className="header">
            <SelectedType type="title" weight="700" capitalize>
              <Translate id="ARTIST_PROFILE.PLANS.PRO" />
            </SelectedType>
            <PositionedType active={true} type="small" weight="700">
              <Translate id="ARTIST_PROFILE.PLANS.ACTIVE" />
            </PositionedType>
          </div>
          <ItemsBox>
            <FeaturesType weight="400" type="small">
              <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.PROFIT" />
            </FeaturesType>
          </ItemsBox>
        </Card>
        <Card active={false}>
          <div className="header">
            <SelectedType type="title" weight="700" capitalize>
              <Translate id="ARTIST_PROFILE.PLANS.FREE" />
            </SelectedType>
            <PositionedType active={false} type="small" weight="700">
              <Translate id="ARTIST_PROFILE.PLANS.DOWNGRADED_PLAN" />
            </PositionedType>
          </div>
          <ItemsBox>
            <Table className="mb-0">
              <Row>
                <Typography className="col-1" type="small">
                  <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.ROYALTY" />
                </Typography>
                <Typography
                  className="col-2"
                  type="small"
                  align="center"
                  style={{ textDecoration: 'line-through' }}
                >
                  <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.ROYALTY_PRO" />
                </Typography>
                <Typography className="col-3" type="small" align="center">
                  <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.ROYALTY_FREE" />
                </Typography>
              </Row>
            </Table>
          </ItemsBox>
        </Card>
        <CustomHint
          // TODO: Integrate date until the user can use the plan
          customTitle={
            <Translate
              id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.HINT.TITLE"
              data={{
                date: moment(nextBillingDate, 'YYYY-MM-DD').format(
                  'D MMM YYYY'
                ),
              }}
            />
          }
        >
          <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.HINT.TEXT" />
        </CustomHint>
        <Flex
          gap={5}
          flexDirection={isMobile ? 'column' : 'row'}
          className="mt-40"
        >
          <Button fullWidth buttonType="primary" onClick={goBack}>
            <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.BTN.CANCEL" />
          </Button>
          <ButtonLoadingCtrl
            fullWidth
            buttonType="alert-secondary"
            className="btn"
            isLoading={false}
            onClick={handleDowngrade}
          >
            <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.BTN.UNSUBSCRIBE" />
          </ButtonLoadingCtrl>
        </Flex>
      </Container>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(UnsubscribePage)`
  position: relative;
  margin-top: 24px;
  min-height: calc(100% - 100px - 24px);
  min-height: calc(100vh - 100px - 24px);

  @media ${({ theme }) => theme.device.laptopS} {
    margin-top: 80px;
    padding-bottom: 50px;
    min-height: calc(100% - 100px - 80px);
    min-height: calc(100vh - 100px - 80px);
  }
`);

const Container = styled.div`
  margin: auto;
  max-width: ${getWidth(6)};

  @media ${({ theme }) => theme.device.tablet} {
    max-width: ${getWidth(10)};
  }
`;

const TitleWrapper = styled.div`
  display: grid;
  grid-gap: 10px;
  margin-bottom: 30px;
`;

const Card = styled.div<{ active: boolean }>`
  padding: 30px;
  position: relative;

  ${({ active }) =>
    !active
      ? css`
          border-radius: 2px 2px 0 0;
          background-color: ${({ theme }) => theme.colors.lightGray};
          span {
            color: ${({ theme }) => theme.colors.primary};
          }
        `
      : css`
          border-radius: 10px 10px 0 0;
          background-color: ${({ theme }) => theme.colors.primary};
          span {
            color: ${({ theme }) => theme.colors.background};
          }
        `}

  .header {
    margin-bottom: 24px;
  }
`;

const PositionedType = styled(Typography)<{ active: boolean }>`
  border-radius: 90px;
  padding: 4px 8px;
  max-width: 150px;
  transform: translate(-8px, 0);
  opacity: 1;
  text-align: center;
  position: absolute;
  top: 30px;
  right: 30px;
  color: ${({ theme }) => theme.colors.black} !important;
  ${({ active }) =>
    !active
      ? css`
          background-color: ${({ theme }) => theme.colors.hint};
        `
      : css`
          background-color: ${({ theme }) => theme.colors.greenLight};
        `}
`;

const Table = styled(Grid)`
  .col-1 {
    grid-column: span 4;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .col-2 {
    grid-column: 5 / 7;
  }

  .col-3 {
    grid-column: 7 / 8;
  }

  .table-header {
    padding-bottom: 20px;
  }

  .table-footer {
    margin-top: 20px;
  }

  .table-header,
  .table-footer {
    align-items: center;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
`;

const Row = styled(Grid)`
  padding: 10px 0;
  align-items: center;
  grid-template-columns: repeat(7, minmax(0, 1fr));
`;

const SelectedType = styled(Typography)`
  color: ${({ theme }) => theme.colors.pureBlackWhite};
`;

const ItemsBox = styled(Flex)`
  flex-direction: column;
`;

const FeaturesType = styled(Typography)`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CustomHint = styled(Hint)`
  margin-top: 3px;
  border-radius: 0 0 10px 10px;
`;
